import { useState, useCallback, useEffect, useRef } from "react";
import dayjs from "dayjs";
import DashboardChart, { ChartData } from "./DashboardChart";
import DynamicFilterPanel, { DynamicFilterConfig } from "../../../components/DynamicComponent/DynamicFilterPanel";
import useApi from "../../../services/useApi";
import endpoints from "../../../services/endpoints";
import DropDownModel from "../../../components/Dropdowns/DropDownModel";
import Image from "../../../components/images/Image";
import DropdownFramer from "../../../components/framerMotion/DropdownFramer";

interface MerchantRechargeRankingData {
  merchant_name: string;
  merchant_code: string;
  transaction_count: number;
  total_recharge_sales: number;
}

interface TopMerchantRechargeRankingApiResponse {
  mno_name: string;
  data: MerchantRechargeRankingData[];
  message?: string;
}

interface MnoOption {
  mno_id: string;
  mno_name: string;
}

// User role options - predefined list
const userRoleOptions = [
  { value: "MDST", label: "Master Distributor" },
  { value: "DST", label: "Distributor" },
  { value: "SDST", label: "Sub Distributor" },
  { value: "AGT", label: "Agent" },
  { value: "RET", label: "Retailer" }
];

const TopMerchantRechargeRanking: React.FC = () => {
  const api = useApi();
  
  // State for chart data
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState<ChartData>({ labels: [], datasets: [] });
  const [error, setError] = useState<string | undefined>(undefined);
  const [merchantData, setMerchantData] = useState<MerchantRechargeRankingData[]>([]); // Store merchant data for tooltip
  const [mnoName, setMnoName] = useState<string>(""); // Store MNO name from API
  const [totalTransactions, setTotalTransactions] = useState<number>(0); // Store total transactions
  const [totalRechargeSales, setTotalRechargeSales] = useState<number>(0); // Store total recharge sales
  const chartLabelsRef = useRef<Array<{ name: string; code: string }>>([]); // Store merchant info for callback
  const hasInitialized = useRef(false);
  const mnoOptionsFetched = useRef(false); // Track if MNO options have been fetched
  
  // State for MNO dropdown
  const [mnoOptions, setMnoOptions] = useState<MnoOption[]>([]);
  const [mnoLoading, setMnoLoading] = useState(false);
  const [mnoDropdownOpen, setMnoDropdownOpen] = useState(false);
  const [selectedMno, setSelectedMno] = useState<MnoOption | null>(null);
  const mnoDropdownRef = useRef<HTMLDivElement>(null);
  const mnoButtonRef = useRef<HTMLButtonElement>(null);
  const [mnoDropdownPositionAbove, setMnoDropdownPositionAbove] = useState(false);
  
  // State for user role dropdown
  const [userRoleDropdownOpen, setUserRoleDropdownOpen] = useState(false);
  const [selectedUserRole, setSelectedUserRole] = useState<string>("Master Distributor");
  const userRoleDropdownRef = useRef<HTMLDivElement>(null);
  const userRoleButtonRef = useRef<HTMLButtonElement>(null);
  const [userRoleDropdownPositionAbove, setUserRoleDropdownPositionAbove] = useState(false);
  
  // Internal search params for filters
  const [searchParams, setSearchParams] = useState<URLSearchParams>(new URLSearchParams());

  // Fetch MNO options
  const fetchMnoOptions = useCallback(async () => {
    // Prevent multiple simultaneous calls
    if (mnoOptionsFetched.current) {
      return;
    }
    
    setMnoLoading(true);
    mnoOptionsFetched.current = true;
    
    try {
      const response = await api.getAPI('mnos?deployment_status=DEPLOYED');
      if (!response.error && response.data?.data) {
        const mnos: MnoOption[] = response.data.data.map((mno: { id: string; name: string }) => ({
          mno_id: mno.id,
          mno_name: mno.name
        }));
        
        // Sort alphabetically by name
        const sortedMnos = mnos.sort((a, b) => a.mno_name.localeCompare(b.mno_name));
        setMnoOptions(sortedMnos);
        
        // Set default to first MNO if not already selected
        setSelectedMno(prev => prev || (sortedMnos.length > 0 ? sortedMnos[0] : null));
      }
    } catch (err) {
      console.error('Error fetching MNO options:', err);
      mnoOptionsFetched.current = false; // Reset on error so it can retry
    } finally {
      setMnoLoading(false);
    }
  }, [api]);

  // Fetch MNO options on mount (only once)
  useEffect(() => {
    if (!mnoOptionsFetched.current) {
      fetchMnoOptions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run on mount

  // Dynamic filter configuration
  const filterConfig: DynamicFilterConfig = {
    "Location": {
      label: "Location",
      type: "dropdown",
      required: false,
      searchKey: "location",
      subFilters: {
        country: {
          label: "Country",
          placeholder: "Select Country",
          searchKey: "country"
        },
        province: {
          label: "Province",
          placeholder: "Select Province",
          searchKey: "province"
        },
        region: {
          label: "Region",
          placeholder: "Select Region",
          searchKey: "region"
        }
      }
    },
    "Duration": {
      label: "Duration",
      type: "dateRangeDropdown",
      required: false,
      searchKey: { 
        date: "date", 
        start: "start_date", 
        end: "end_date" 
      },
    }
  };

  // Function to process API data for chart
  const processDataForChart = useCallback((apiData: TopMerchantRechargeRankingApiResponse): ChartData => {
    if (!apiData.data || apiData.data.length === 0) {
      return { labels: [], datasets: [] };
    }

    // Filter out merchants with missing names or codes, and get top 10 valid merchants
    const validMerchants = apiData.data
      .filter(merchant => 
        merchant && 
        merchant.merchant_name && 
        merchant.merchant_code && 
        merchant.merchant_name.trim() !== '' && 
        merchant.merchant_code.trim() !== ''
      )
      .slice(0, 10);
    
    if (validMerchants.length === 0) {
      return { labels: [], datasets: [] };
    }
    
    // Calculate total transactions and total recharge sales
    const totalTrans = validMerchants.reduce((sum, merchant) => sum + (merchant.transaction_count || 0), 0);
    const totalSales = validMerchants.reduce((sum, merchant) => sum + (merchant.total_recharge_sales || 0), 0);
    setTotalTransactions(totalTrans);
    setTotalRechargeSales(totalSales);
    
    // Create labels array with fallback values
    const labels = validMerchants.map(merchant => {
      const name = merchant.merchant_name?.trim() || 'Unknown Merchant';
      const code = merchant.merchant_code?.trim() || 'N/A';
      return `${name} (${code})`;
    });
    
    // Store merchant info for multi-line formatting in callback
    // Ensure we store all merchants with proper fallbacks
    chartLabelsRef.current = validMerchants.map(merchant => {
      const name = merchant.merchant_name ? merchant.merchant_name.trim() : 'Unknown Merchant';
      const code = merchant.merchant_code ? merchant.merchant_code.trim() : 'N/A';
      return {
        name: name || 'Unknown Merchant',
        code: code || 'N/A'
      };
    });
    
    // Create data array with total_recharge_sales (Amount)
    const rechargeSalesData = validMerchants.map(merchant => merchant.total_recharge_sales || 0);

    return {
      labels,
      datasets: [{
        label: "Total Recharge Sales",
        data: rechargeSalesData,
        backgroundColor: 'rgba(139, 92, 246, 0.8)', // Purple color matching design
        borderColor: 'rgba(139, 92, 246, 1)',
        borderWidth: 1,
        minBarLength: 5, // The bar will always be at least 5px tall
      }]
    };
  }, []);

  // Function to fetch merchant recharge ranking data
  const fetchMerchantRechargeRanking = useCallback(async (filters: { [key: string]: string | string[] | boolean | null }, userRole: string = "MDST", mnoId: string | null = null) => {
    setLoading(true);
    setError(undefined);
    
    try {
      // Build query parameters
      const params = new URLSearchParams();
      
      // Add MNO ID (required)
      if (mnoId) {
        params.append('mno_id', mnoId);
      } else if (selectedMno) {
        params.append('mno_id', selectedMno.mno_id);
      } else {
        setError('Please select an MNO');
        setLoading(false);
        return;
      }
      
      // Add user role (required, default MDST)
      const role = filters.user_role as string || userRole || "MDST";
      params.append('user_role', role);
      
      // Add date range
      if (filters.start_date) {
        const formattedStartDate = dayjs(filters.start_date as string).format('YYYY-MM-DD');
        params.append('start_date', formattedStartDate);
      }
      if (filters.end_date) {
        const formattedEndDate = dayjs(filters.end_date as string).format('YYYY-MM-DD');
        params.append('end_date', formattedEndDate);
      }
      
      // Add location filters
      if (filters.country) {
        params.append('country', filters.country as string);
      }
      if (filters.province) {
        params.append('province', filters.province as string);
      }
      if (filters.region) {
        params.append('region', filters.region as string);
      }
      
      // Add client timezone
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      params.append('client_timezone', timezone);
      
      const endpoint = `${endpoints.topMerchantRechargeRanking}?${params.toString()}`;
      const response = await api.getAPI(endpoint);
      
      if (response.error) {
        const errorMessage = response.data?.message || `Failed to fetch merchant recharge ranking data for ${role}`;
        setError(errorMessage);
        setChartData({ labels: [], datasets: [] });
        setMnoName("");
        setTotalTransactions(0);
        setTotalRechargeSales(0);
        return;
      }
      
      const apiData = response.data as TopMerchantRechargeRankingApiResponse;
      
      // Check if data is empty
      if (!apiData.data || apiData.data.length === 0) {
        setError('NO_DATA');
        setChartData({ labels: [], datasets: [] });
        setMnoName(apiData.mno_name || "");
        setTotalTransactions(0);
        setTotalRechargeSales(0);
        return;
      }
      
      // Store MNO name
      setMnoName(apiData.mno_name || "");
      
      // Filter and store valid merchant data for tooltip (same as what's used in chart)
      const validMerchants = apiData.data
        .filter(merchant => 
          merchant && 
          merchant.merchant_name && 
          merchant.merchant_code && 
          merchant.merchant_name.trim() !== '' && 
          merchant.merchant_code.trim() !== ''
        )
        .slice(0, 10);
      
      // Store only the valid merchants that will be displayed in the chart
      setMerchantData(validMerchants);
      
      // Process the API data for chart
      const processedData = processDataForChart(apiData);
      setChartData(processedData);
      setError(undefined);
      
    } catch (err) {
      console.error('Error fetching merchant recharge ranking:', err);
      setError('An error occurred while fetching data');
      setChartData({ labels: [], datasets: [] });
      setMnoName("");
      setTotalTransactions(0);
      setTotalRechargeSales(0);
    } finally {
      setLoading(false);
    }
  }, [api, processDataForChart, selectedMno]);

  // Handle MNO change
  const handleMnoChange = useCallback((mno: MnoOption) => {
    // If the same MNO is selected, just close the dropdown and return
    if (selectedMno?.mno_id === mno.mno_id) {
      setMnoDropdownOpen(false);
      return;
    }
    
    setSelectedMno(mno);
    setMnoDropdownOpen(false);
    
    // Get current filters and update with new MNO
    const currentFilters: { [key: string]: string | string[] | boolean | null } = {};
    
    // Get current user role
    const currentRoleOption = userRoleOptions.find(r => r.label === selectedUserRole);
    const currentUserRole = currentRoleOption?.value || "MDST";
    currentFilters.user_role = currentUserRole;
    
    // Add existing filter values from searchParams
    if (searchParams.get('start_date')) {
      currentFilters.start_date = searchParams.get('start_date') as string;
    }
    if (searchParams.get('end_date')) {
      currentFilters.end_date = searchParams.get('end_date') as string;
    }
    if (searchParams.get('country')) {
      currentFilters.country = searchParams.get('country') as string;
    }
    if (searchParams.get('province')) {
      currentFilters.province = searchParams.get('province') as string;
    }
      if (searchParams.get('region')) {
        currentFilters.region = searchParams.get('region') as string;
      }
      
      fetchMerchantRechargeRanking(currentFilters, currentUserRole, mno.mno_id);
  }, [searchParams, fetchMerchantRechargeRanking, selectedUserRole, selectedMno]);

  // Handle user role change
  const handleUserRoleChange = useCallback((roleLabel: string) => {
    // If the same user role is selected, just close the dropdown and return
    if (selectedUserRole === roleLabel) {
      setUserRoleDropdownOpen(false);
      return;
    }
    
    const roleOption = userRoleOptions.find(r => r.label === roleLabel);
    if (roleOption) {
      setSelectedUserRole(roleLabel);
      setUserRoleDropdownOpen(false);
      
      // Get current filters and update with new user role
      const currentFilters: { [key: string]: string | string[] | boolean | null } = {
        user_role: roleOption.value
      };
      
      // Add existing filter values from searchParams
      if (searchParams.get('start_date')) {
        currentFilters.start_date = searchParams.get('start_date') as string;
      }
      if (searchParams.get('end_date')) {
        currentFilters.end_date = searchParams.get('end_date') as string;
      }
      if (searchParams.get('country')) {
        currentFilters.country = searchParams.get('country') as string;
      }
      if (searchParams.get('province')) {
        currentFilters.province = searchParams.get('province') as string;
      }
      if (searchParams.get('region')) {
        currentFilters.region = searchParams.get('region') as string;
      }
      
      const mnoId = selectedMno?.mno_id || null;
      fetchMerchantRechargeRanking(currentFilters, roleOption.value, mnoId);
    }
  }, [searchParams, fetchMerchantRechargeRanking, selectedMno, selectedUserRole]);

  // Filter handler
  const handleFilterApply = useCallback((filters: { [key: string]: string | string[] | boolean | null }) => {
    console.log('Merchant Recharge Ranking Filters applied:', filters);
    
    // Update internal search params
    const newSearchParams = new URLSearchParams(searchParams);
    
    // Clear existing filter parameters
    newSearchParams.delete('date');
    newSearchParams.delete('start_date');
    newSearchParams.delete('end_date');
    newSearchParams.delete('country');
    newSearchParams.delete('province');
    newSearchParams.delete('region');
    
    // Process filters
    const processedFilters: { [key: string]: string | string[] | boolean | null } = {};
    
    // Get current user role from selectedUserRole
    const currentRoleOption = userRoleOptions.find(r => r.label === selectedUserRole);
    const currentUserRole = currentRoleOption?.value || "MDST";
    processedFilters.user_role = currentUserRole;
    newSearchParams.set('user_role', currentUserRole);
    
    // Handle Duration filter (dateRangeDropdown)
    if (filters.date) {
      newSearchParams.set('date', filters.date as string);
      
      switch (filters.date) {
        case 'Today': {
          const today = dayjs();
          processedFilters.start_date = today.startOf('day').format('YYYY-MM-DD');
          processedFilters.end_date = today.endOf('day').format('YYYY-MM-DD');
          newSearchParams.set('start_date', processedFilters.start_date as string);
          newSearchParams.set('end_date', processedFilters.end_date as string);
          break;
        }
        case 'Yesterday': {
          const yesterday = dayjs().subtract(1, 'day');
          processedFilters.start_date = yesterday.startOf('day').format('YYYY-MM-DD');
          processedFilters.end_date = yesterday.endOf('day').format('YYYY-MM-DD');
          newSearchParams.set('start_date', processedFilters.start_date as string);
          newSearchParams.set('end_date', processedFilters.end_date as string);
          break;
        }
        case 'This Month': {
          const today = dayjs();
          processedFilters.start_date = today.startOf('month').format('YYYY-MM-DD');
          processedFilters.end_date = today.endOf('month').format('YYYY-MM-DD');
          newSearchParams.set('start_date', processedFilters.start_date as string);
          newSearchParams.set('end_date', processedFilters.end_date as string);
          break;
        }
        case 'This Year': {
          const today = dayjs();
          processedFilters.start_date = today.startOf('year').format('YYYY-MM-DD');
          processedFilters.end_date = today.endOf('year').format('YYYY-MM-DD');
          newSearchParams.set('start_date', processedFilters.start_date as string);
          newSearchParams.set('end_date', processedFilters.end_date as string);
          break;
        }
        case 'Custom': {
          if (filters.start_date) {
            const formattedStartDate = dayjs(filters.start_date as string).format('YYYY-MM-DD');
            processedFilters.start_date = formattedStartDate;
            newSearchParams.set('start_date', formattedStartDate);
          }
          if (filters.end_date) {
            const formattedEndDate = dayjs(filters.end_date as string).format('YYYY-MM-DD');
            processedFilters.end_date = formattedEndDate;
            newSearchParams.set('end_date', formattedEndDate);
          }
          break;
        }
      }
    }
    
    // Handle location filters
    if (filters.country) {
      processedFilters.country = filters.country as string;
      newSearchParams.set('country', filters.country as string);
    }
    if (filters.province) {
      processedFilters.province = filters.province as string;
      newSearchParams.set('province', filters.province as string);
    }
    if (filters.region) {
      processedFilters.region = filters.region as string;
      newSearchParams.set('region', filters.region as string);
    }
    
    // Update internal search params
    setSearchParams(newSearchParams);
    
    // Fetch data with processed filters
    const mnoId = selectedMno?.mno_id || null;
    fetchMerchantRechargeRanking(processedFilters, processedFilters.user_role as string, mnoId);
  }, [searchParams, fetchMerchantRechargeRanking, selectedUserRole, selectedMno]);

  // Initialize with default data on component mount
  useEffect(() => {
    if (!hasInitialized.current && selectedMno) {
      hasInitialized.current = true;
      
      // Set default filters (only user role and MNO, no date filters)
      const defaultFilters = {
        user_role: "MDST"
      };
      
      fetchMerchantRechargeRanking(defaultFilters, "MDST", selectedMno.mno_id);
    }
  }, [fetchMerchantRechargeRanking, selectedMno]);

  // Function to calculate dropdown position
  const calculateDropdownPosition = useCallback((buttonRef: React.RefObject<HTMLButtonElement>) => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownMaxHeight = 240;
      const gap = 4;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      const shouldPositionAbove = (spaceBelow < dropdownMaxHeight + gap) && spaceAbove > spaceBelow && spaceAbove >= dropdownMaxHeight;
      
      return shouldPositionAbove;
    }
    return false;
  }, []);

  // Handle MNO dropdown toggle
  const handleMnoDropdownToggle = useCallback(() => {
    if (!mnoDropdownOpen) {
      const shouldPositionAbove = calculateDropdownPosition(mnoButtonRef);
      setMnoDropdownPositionAbove(shouldPositionAbove);
    }
    setMnoDropdownOpen(prev => !prev);
  }, [mnoDropdownOpen, calculateDropdownPosition]);

  // Handle user role dropdown toggle
  const handleUserRoleDropdownToggle = useCallback(() => {
    if (!userRoleDropdownOpen) {
      const shouldPositionAbove = calculateDropdownPosition(userRoleButtonRef);
      setUserRoleDropdownPositionAbove(shouldPositionAbove);
    }
    setUserRoleDropdownOpen(prev => !prev);
  }, [userRoleDropdownOpen, calculateDropdownPosition]);

  // Close dropdowns when clicking or scrolling outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (mnoDropdownRef.current && !mnoDropdownRef.current.contains(event.target as Node)) {
        setMnoDropdownOpen(false);
      }
      if (userRoleDropdownRef.current && !userRoleDropdownRef.current.contains(event.target as Node)) {
        setUserRoleDropdownOpen(false);
      }
    };

    const handleScroll = (event: Event) => {
      const target = event.target as HTMLElement;
      if (target) {
        if (mnoDropdownRef.current) {
          const isScrollFromDropdown = mnoDropdownRef.current.contains(target);
          const isDropdownScrollableArea = target.closest('.overflow-y-auto') && mnoDropdownRef.current.contains(target.closest('.overflow-y-auto'));
          
          if (!isScrollFromDropdown && !isDropdownScrollableArea) {
            setMnoDropdownOpen(false);
          }
        }
        if (userRoleDropdownRef.current) {
          const isScrollFromDropdown = userRoleDropdownRef.current.contains(target);
          const isDropdownScrollableArea = target.closest('.overflow-y-auto') && userRoleDropdownRef.current.contains(target.closest('.overflow-y-auto'));
          
          if (!isScrollFromDropdown && !isDropdownScrollableArea) {
            setUserRoleDropdownOpen(false);
          }
        }
      }
    };

    if (mnoDropdownOpen || userRoleDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [mnoDropdownOpen, userRoleDropdownOpen]);

  return (
    <div className="w-full">
      <DashboardChart
        type="bar"
        data={chartData}
        height={620}
        showLegend={false}
        loading={loading}
        error={error}
        noDataMessage="No Data Available For The Selected User Role"
        noDataSubMessage="Please apply the appropriate filters to display top 10 merchant wise recharge sales ranking"
        topbar={{
          title: "Top 10 Merchant Wise Recharge Sales Ranking",
          rightContent: (
            <div className="flex flex-wrap items-center gap-2">
              {/* MNO Dropdown */}
              <DropDownModel 
                isOpen={mnoDropdownOpen} 
                handleClose={() => setMnoDropdownOpen(false)}
              >
                <div ref={mnoDropdownRef} className="relative">
                  <button
                    ref={mnoButtonRef}
                    type="button"
                    onClick={handleMnoDropdownToggle}
                    disabled={mnoLoading || mnoOptions.length === 0}
                    className="bg-white h-10 px-4 py-2 border border-P_light_active rounded-lg text-subTitle outline-none focus:border-2 flex items-center justify-between gap-2 w-[140px] md:w-[160px] lg:w-[180px] xl:w-[200px] 2xl:w-[250px] min-w-[140px] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="text-primary_text truncate">
                      {mnoLoading ? "Loading..." : selectedMno ? selectedMno.mno_name : "Select MNO"}
                    </span>
                    <DropdownFramer isDropdownVisible={mnoDropdownOpen}>
                      <Image src="dropdown-down-arrow" />
                    </DropdownFramer>
                  </button>
                  {mnoDropdownOpen && (
                    <div className={`absolute left-0 w-full bg-white border border-P_light_active rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto ${
                      mnoDropdownPositionAbove ? 'bottom-full mb-1' : 'top-full mt-1'
                    }`}>
                      {mnoOptions.map((mno) => (
                        <div
                          key={mno.mno_id}
                          onClick={() => handleMnoChange(mno)}
                          className={`p-2.5 text-subTitle cursor-pointer hover:bg-information_light hover:rounded-lg ${
                            selectedMno?.mno_id === mno.mno_id ? 'bg-information_light text-primary_text' : 'text-primary_text'
                          }`}
                        >
                          {mno.mno_name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </DropDownModel>
              
              {/* User Role Dropdown */}
              <DropDownModel 
                isOpen={userRoleDropdownOpen} 
                handleClose={() => setUserRoleDropdownOpen(false)}
              >
                <div ref={userRoleDropdownRef} className="relative">
                  <button
                    ref={userRoleButtonRef}
                    type="button"
                    onClick={handleUserRoleDropdownToggle}
                    className="bg-white h-10 px-4 py-2 border border-P_light_active rounded-lg text-subTitle outline-none focus:border-2 flex items-center justify-between gap-2 w-[120px] md:w-[140px] lg:w-[160px] xl:w-[180px] 2xl:w-[220px] min-w-[120px]"
                  >
                    <span className="text-primary_text truncate">{selectedUserRole}</span>
                    <DropdownFramer isDropdownVisible={userRoleDropdownOpen}>
                      <Image src="dropdown-down-arrow" />
                    </DropdownFramer>
                  </button>
                  {userRoleDropdownOpen && (
                    <div className={`absolute left-0 w-full bg-white border border-P_light_active rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto ${
                      userRoleDropdownPositionAbove ? 'bottom-full mb-1' : 'top-full mt-1'
                    }`}>
                      {userRoleOptions.map((role) => (
                        <div
                          key={role.value}
                          onClick={() => handleUserRoleChange(role.label)}
                          className={`p-2.5 text-subTitle cursor-pointer hover:bg-information_light hover:rounded-lg ${
                            selectedUserRole === role.label ? 'bg-information_light text-primary_text' : 'text-primary_text'
                          }`}
                        >
                          {role.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </DropDownModel>
              
              {/* Filter Panel */}
              <div className="flex-shrink-0">
                <DynamicFilterPanel
                  config={filterConfig}
                  onApply={handleFilterApply}
                  onClear={() => {
                    console.log('Merchant Recharge Ranking filters cleared');
                    const newSearchParams = new URLSearchParams();
                    const currentRoleOption = userRoleOptions.find(r => r.label === selectedUserRole);
                    const currentUserRole = currentRoleOption?.value || "MDST";
                    newSearchParams.set('user_role', currentUserRole);
                    setSearchParams(newSearchParams);
                    const defaultFilters = {
                      user_role: currentUserRole
                    };
                    const mnoId = selectedMno?.mno_id || null;
                    fetchMerchantRechargeRanking(defaultFilters, currentUserRole, mnoId);
                  }}
                  loading={loading}
                  searchParams={searchParams}
                  customHeight="h-[160px]"
                />
              </div>
            </div>
          )
        }}
        belowTopbar={mnoName && (
          <div className="w-full px-4 py-4">
            <div className="overflow-x-auto">
            <table className="min-w-full border border-[#D8DFE9]">
              <thead className="bg-p_light">
                <tr>
                  <th scope="col" className="px-6 py-2.5 text-left text-sm font-bold text-gray-900 tracking-wider border border-[#D8DFE9]">
                    MNO Name
                  </th>
                  <th scope="col" className="px-6 py-2.5 text-left text-sm font-bold text-gray-900 tracking-wider border border-[#D8DFE9]">
                    Total Number Of Transactions
                  </th>
                  <th scope="col" className="px-6 py-2.5 text-left text-sm font-bold text-gray-900 tracking-wider border border-[#D8DFE9]">
                    Total Recharge Sales
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white">
                <tr>
                  <td className="px-6 py-2.5 whitespace-nowrap text-sm font-medium text-primary_text border border-[#D8DFE9]">
                    {mnoName}
                  </td>
                  <td className="px-6 py-2.5 whitespace-nowrap text-sm font-medium text-primary_text border border-[#D8DFE9]">
                    {totalTransactions}
                  </td>
                  <td className="px-6 py-2.5 whitespace-nowrap text-sm font-medium text-primary_text border border-[#D8DFE9]">
                    AFN {totalRechargeSales.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                </tr>
              </tbody>
            </table>
            </div>
          </div>
        )}
        options={{
          scales: {
            x: {
              display: true,
              grid: {
                display: true,
                color: 'rgba(0, 0, 0, 0.02)',
                lineWidth: 1
              },
              ticks: {
                font: {
                  size: 11,
                  family: 'Montserrat, sans-serif',
                  weight: 'bold'
                },
                color: '#666',
                maxRotation: 45,
                minRotation: 45,
                padding: 15, // Increased padding for two-line labels
                autoSkip: false, // Don't auto-skip any labels
                maxTicksLimit: undefined, // Remove limit on number of ticks
                callback: function(_value: number | string, index: number) {
                  // Ensure we have a valid merchant at this index
                  if (index >= 0 && index < chartLabelsRef.current.length) {
                    const merchant = chartLabelsRef.current[index];
                    if (merchant) {
                      const name = (merchant.name && merchant.name.trim()) || 'Unknown Merchant';
                      const code = (merchant.code && merchant.code.trim()) || 'N/A';
                      
                      // Return two-line format: name on first line, code on second line
                      return [name, `(${code})`];
                    }
                  }
                  // Fallback: try to get from chartData labels if ref is not available
                  if (index >= 0 && index < chartData.labels.length) {
                    return chartData.labels[index];
                  }
                  return ['Unknown Merchant', '(N/A)'];
                }
              } as Record<string, unknown>
            },
            y: {
              display: true,
              beginAtZero: true,
              title: {
                display: true,
                text: 'Total Recharge Sales (Amount)'
              },
              grid: {
                display: true,
                color: 'rgba(0, 0, 0, 0.02)',
                lineWidth: 1
              },
              ticks: {
                font: {
                  size: 12,
                  family: 'Montserrat, sans-serif',
                  weight: '600'
                },
                color: '#666',
                callback: function(value: number | string) {
                  const numValue = Number(value);
                  if (numValue >= 1000) {
                    const kValue = numValue / 1000;
                    // If it's a whole number, show without decimals (e.g., 1K, 2K, 5K)
                    // Otherwise show one decimal place (e.g., 1.5K, 2.3K)
                    const formattedValue = kValue % 1 === 0 ? kValue.toString() : kValue.toFixed(1);
                    return `AFA ${formattedValue}K`;
                  }
                  return `AFA ${numValue}`;
                }
              }
            }
          },
          elements: {
            bar: {
              borderWidth: 1
            }
          },
          barPercentage: chartData.labels.length === 1 ? 0.2 : chartData.labels.length === 2 ? 0.3 : 0.5,
          categoryPercentage: chartData.labels.length === 1 ? 0.2 : chartData.labels.length === 2 ? 0.3 : 0.5,
          plugins: {
            tooltip: {
              enabled: true,
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              titleColor: '#fff',
              bodyColor: '#fff',
              borderColor: 'rgba(255, 255, 255, 0.1)',
              borderWidth: 1,
              displayColors: false,
              callbacks: {
                title: function() {
                  return 'Total Recharge Sales';
                },
                label: function(context: { dataIndex: number; parsed: { y: number }; label: string }) {
                  const index = context.dataIndex;
                  const merchant = merchantData[index];
                  const totalSales = context.parsed.y || 0;
                  const transactionCount = merchant?.transaction_count || 0;
                  
                  // Get merchant name and code with fallbacks
                  const merchantName = merchant?.merchant_name?.trim() || 'Unknown Merchant';
                  const merchantCode = merchant?.merchant_code?.trim() || 'N/A';
                  
                  // Format amount with K notation if >= 1000
                  let formattedAmount: string;
                  if (totalSales >= 1000) {
                    const kValue = totalSales / 1000;
                    const formattedValue = kValue % 1 === 0 ? kValue.toString() : kValue.toFixed(1);
                    formattedAmount = `AFA ${formattedValue}K`;
                  } else {
                    formattedAmount = `AFA ${totalSales.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                  }
                  
                  // Return merchant name with total recharge sales and transaction count
                  return [
                    `${merchantName} (${merchantCode})`,
                    `Total Recharge Sales: ${formattedAmount}`,
                    `No. of Transactions: ${transactionCount}`
                  ];
                }
              } as Record<string, unknown>
            } as Record<string, unknown>
          }
        }}
      />
    </div>
  );
};

export default TopMerchantRechargeRanking;
